import { useEffect, useRef } from 'react';
import SVG from 'react-inlinesvg';

import StatusRenderer from 'components/common/StatusRenderer';
import ImageRenderer from 'components/common/ImageRenderer';
import { CreativeStatus } from 'components/common/types/Creative.types';
import { TARGET } from 'components/common/ImageRenderer/ImageRenderer.types';
import Table from 'lib/Table/Table';
import { TableHeight, TableRowHeight } from 'lib/Table/Table.types';
import { fetchCreatives } from 'modules/api/content';
import Local from 'modules/Local';
import { CREATIVE_STATUS_BADGE, REJECTION_STATUS_BADGE, REJECTED_TYPE } from 'consts/creative';
import emptyListCreativesSvg from 'assets/icons/empty-list-creatives.svg';
import eyeSvg from 'assets/icons/eye_empty.svg';
import { cancelFunctions } from 'utils/cancelFunctions';
import { GetRowIdParams, GridApi, ICellRendererParams, IDatasource, SelectionChangedEvent } from 'ag-grid-community';
import { CreativesTableProps } from './CreativesTable.type';

import { mapFiltersToRequestData, mapSelectedRowToCreative, prepareCreativesList } from './utils';

const columnDefs = [
  {
    headerName: 'Status',
    field: 'status',
    minWidth: 150,
    headerTooltip: 'Status',
    cellRenderer: ({ data, value }: ICellRendererParams) => {
      if (value === CreativeStatus.REJECTED) {
        const currentMarket = data?.market?.find(
          ({ environment }: { environment: string }) => environment === Local.getEnvironmentId(),
        );

        return (
          <StatusRenderer
            value={currentMarket?.moderation?.[0]?.automatic ? REJECTED_TYPE.AUTO : REJECTED_TYPE.MANUAL}
            statuses={REJECTION_STATUS_BADGE}
          />
        );
      }

      return <StatusRenderer value={value} statuses={CREATIVE_STATUS_BADGE} />;
    },
    colId: 'status',
  },
  {
    headerName: 'Creative',
    field: 'thumbnail',
    minWidth: 150,
    headerTooltip: 'Creative',
    cellRenderer: ImageRenderer,
    cellRendererParams: (params: ICellRendererParams) => ({
      target: TARGET.BLANK,
      hoverIcon: <SVG src={eyeSvg} />,
      link: params.data?.downloadURL || '',
      isExternalLink: true,
    }),
  },
  { headerName: 'Creative ID', field: 'externalId', minWidth: 160, headerTooltip: 'ID' },
  {
    headerName: 'Size (W x H)',
    field: 'size',
    minWidth: 180,
    headerTooltip: 'SIZE (Width X Height)',
  },
  {
    headerName: 'DSP',
    field: 'dsp',
    minWidth: 190,
    headerTooltip: 'DSP',
    cellRenderer: (params: ICellRendererParams) => params.data?.dsp?.name || '',
  },
  {
    headerName: 'Advertiser',
    field: 'advertiser',
    minWidth: 150,
    headerTooltip: 'Advertiser',
    cellRenderer: (params: ICellRendererParams) => params.data?.advertiser?.name || '',
  },
  {
    headerName: 'Brand',
    field: 'brand',
    minWidth: 130,
    headerTooltip: 'Brand',
    cellRenderer: (params: ICellRendererParams) => params.data?.brand?.name || '',
  },
  { headerName: 'Creative type', field: 'type', minWidth: 130, headerTooltip: 'Creative type' },
];

const CreativesTable = ({ filters, onSelectCreative, panel }: CreativesTableProps): React.ReactElement => {
  const PAGE_SIZE = 20;
  const tableApiRef = useRef<GridApi>();

  const getDataSource = (): IDatasource => ({
    getRows: async (params) => {
      const { content, totalElements } = await fetchCreatives(cancelFunctions, {
        ...mapFiltersToRequestData(filters),
        page: params.startRow / PAGE_SIZE,
        size: PAGE_SIZE,
        environment: Local.getEnvironmentId(),
        deleted: false,
        width: panel?.width,
        height: panel?.height,
      });

      if (totalElements === 0) {
        tableApiRef.current?.showNoRowsOverlay();

        params.successCallback(content, totalElements);
        return;
      }

      tableApiRef.current?.hideOverlay();

      params.successCallback(prepareCreativesList(content), totalElements);

      const row = panel?.creative?.id ? tableApiRef.current?.getRowNode(panel?.creative?.id) : null;
      if (row) {
        row.setSelected(true, true);
        onSelectCreative(mapSelectedRowToCreative(row.data));
      } else {
        onSelectCreative(undefined);
      }
    },
  });

  useEffect(() => {
    tableApiRef.current?.setDatasource(getDataSource());
  }, [filters, panel?.id]);

  const getTableApi = (tableApi: GridApi): void => {
    tableApiRef.current = tableApi;
    tableApi.setDatasource(getDataSource());
  };

  const onSelectionChanged = (event: SelectionChangedEvent): void => {
    const [selectedRow] = event.api.getSelectedRows();

    if (selectedRow) {
      onSelectCreative(mapSelectedRowToCreative(selectedRow));
    }
  };

  return (
    <div>
      {!filters.dsp ? (
        <div className="flex flex-1 flex-col justify-center items-center bg-neutral-100 border rounded-lg border-neutral-100-opacity-10 text-neutral-950-opacity-60 py-20">
          <SVG src={emptyListCreativesSvg} />
          <p className="header-lg mt-10 mb-4">Select a DSP</p>
          <p className="body-base	text-center">
            We cannot find the assets you are looking for. Please, try a different criteria to filter.
          </p>
        </div>
      ) : (
        <Table
          dataTestId="creatives-detail-table"
          key={PAGE_SIZE}
          columnDefs={columnDefs}
          getTableApi={getTableApi}
          extraGridOptions={{
            rowModelType: 'infinite',
            rowHeight: TableRowHeight.NORMAL,
            pagination: true,
            paginationPageSize: PAGE_SIZE,
            cacheBlockSize: PAGE_SIZE,
            enableCellTextSelection: true,
            rowSelection: 'single',
            onSelectionChanged,
            getRowId: (params: GetRowIdParams) => params.data.id,
          }}
          tableHeight={TableHeight.AUTO}
        />
      )}
    </div>
  );
};

export default CreativesTable;
